import { useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { createAuthFetch } from "./apiFetch";
import { Role } from "./AuthContext";

interface UserRow {
  id: number;
  username: string;
  roles: Role[];
}

const ALL_ROLES: Role[] = ["author", "moderator", "admin"];

export default function AdminUserManagement() {
  const { token, user: currentUser } = useAuth();
  const apiFetch = useMemo(() => createAuthFetch(token), [token]);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingUsername, setSavingUsername] = useState<string | null>(null);

  const loadUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/api/users");
      if (!res.ok) throw new Error(`Fehler beim Laden (Status ${res.status})`);
      setUsers(await res.json());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Nutzer konnten nicht geladen werden");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const toggleRole = async (username: string, role: Role, hasIt: boolean) => {
    const targetUser = users.find((u) => u.username === username);
    if (!targetUser) return;

    const newRoles = hasIt ? targetUser.roles.filter((r) => r !== role) : [...targetUser.roles, role];

    setSavingUsername(username);
    setError(null);
    try {
      const res = await apiFetch(`/api/users/${encodeURIComponent(username)}/roles`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ roles: newRoles }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error ?? "Rollen konnten nicht aktualisiert werden");
      }
      const updated = await res.json();
      setUsers((prev) => prev.map((u) => (u.username === username ? { ...u, roles: updated.roles } : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rollen konnten nicht aktualisiert werden");
    } finally {
      setSavingUsername(null);
    }
  };

  return (
    <div className="admin-users-page">
        <div className="admin-users-header">
        <div>
            <h1>Nutzerverwaltung</h1>
            <p>
            Verwalte Benutzer und ihre Berechtigungen.
            </p>
        </div>

        <button
            type="button"
            className="btn-secondary"
            onClick={loadUsers}
            disabled={isLoading}
        >
            {isLoading ? "Lädt..." : "Aktualisieren"}
        </button>
        </div>

        {error && (
        <div className="admin-users-error">
            {error}
        </div>
        )}

        <div className="admin-users-card">
        {isLoading ? (
            <div className="admin-users-loading">
            Nutzer werden geladen...
            </div>
        ) : users.length === 0 ? (
            <div className="admin-users-empty">
            Keine Nutzer vorhanden.
            </div>
        ) : (
            <table className="admin-user-table">
            <thead>
                <tr>
                <th>Benutzer</th>

                {ALL_ROLES.map((role) => (
                    <th key={role}>
                    {role === "author" && "Autor"}
                    {role === "moderator" && "Moderator"}
                    {role === "admin" && "Admin"}
                    </th>
                ))}
                </tr>
            </thead>

            <tbody>
                {users.map((u) => {
                const isCurrentUser =
                    u.username === currentUser?.username;

                const isSaving =
                    savingUsername === u.username;

                return (
                    <tr key={u.id}>
                    <td>
                        <div className="admin-user-identity">
                        <span className="admin-user-name">
                            {u.username}
                        </span>

                        {isCurrentUser && (
                            <span className="admin-user-self">
                            Du
                            </span>
                        )}
                        </div>
                    </td>

                    {ALL_ROLES.map((role) => {
                        const hasIt = u.roles.includes(role);

                        return (
                        <td key={role}>
                            <label className="role-toggle">
                            <input
                                type="checkbox"
                                checked={hasIt}
                                disabled={isSaving}
                                onChange={() =>
                                toggleRole(
                                    u.username,
                                    role,
                                    hasIt
                                )
                                }
                            />

                            <span className="role-toggle__visual" />
                            </label>
                        </td>
                        );
                    })}
                    </tr>
                );
                })}
            </tbody>
            </table>
        )}
        </div>
    </div>
    );
}